import type { ReactNode } from 'react';

import { useAuth } from 'react-oidc-context';

import { isTestLoginEnabled } from './config';
import { TestLoginRoute } from '@/routes/TestLoginRoute';

interface TestLoginGateProps {
  children: ReactNode;
}

/**
 * Dev-only gate that fronts the app when test login is enabled
 * (`VITE_TEST_LOGIN_ENABLED=true`). Until a test user is authenticated it
 * renders only the login form, so the rest of the app — `MainRouter`'s
 * feature-flag queries, PostHog, the digital-adoption plugin — never mounts and
 * fires background requests mid-login. This mirrors how the production OIDC
 * flow blocks by navigating off the SPA to the login portal.
 *
 * When test login is disabled this is a pure passthrough, so the production
 * path is unaffected.
 */
export const TestLoginGate = ({ children }: TestLoginGateProps) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (!isTestLoginEnabled()) return <>{children}</>;

  // Block render while the OIDC store is still being read on mount.
  if (isLoading) return null;

  if (!isAuthenticated) return <TestLoginRoute />;

  return <>{children}</>;
};
